// KontaktAuswahlPopup.tsx — Kontaktauswahl in den Risiko-Formularen
// (Eigentümer, Mieter, Verwaltung, Gutachter, Darlehensgeber …).
//
// Bewusst KEIN Fluent-Dialog: Gewählt wird derselbe Kontakt wie in der
// Kundenakte („Verbindung zu Firma"), also laufen hier auch dieselben
// Bausteine — `createPortal` auf `document.body` (Muster
// `redesign/RelationCreateDialog`) plus `redesign/RelationContactPicker` als
// Auswahl. Damit gibt es genau EINE Kontaktsuche im Produkt statt zweier, die
// auseinanderlaufen.
//
// Die Optik kommt aus `contactRedesign.css`. Deren Regeln greifen
// ausschließlich unter `.kvp-rd` bzw. `.kvp-rd-scrim`, leaken also nicht in
// das Griffel-gestylte Risiko-Formular; importiert wird das Stylesheet hier,
// weil die Risiko-Seiten es selbst nicht laden.
//
// Was der abgelöste Fluent-Dialog mitbrachte und hier von Hand nachgebaut
// ist (gleiche Begründung wie in `windowManager/WindowConfirmDialog`):
// z-index über dem Fenster-Layer, Fokus beim Öffnen/Schließen, Fokusfalle
// und die Scroll-Sperre im Hintergrund.
//
// ACHTUNG — Datenschutz: Diese Komponente sucht im Kontaktbestand
// (`GET /contact?search`). Sie gehört ausschließlich in den
// authentifizierten Weg. Der öffentliche Token-Weg (Kunde füllt selbst aus)
// darf sie NICHT rendern; das Gate dafür sitzt in `KontaktFeld` (`lookup`)
// und wird in `RisikoImmobilieStepForm` aus `chrome === "embedded"` gebildet.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { RelationContactPicker } from "../redesign/RelationContactPicker";
import { Z_LAYERS } from "../windowManager/zLayers";
import "../redesign/contactRedesign.css";
import { alleDokumente, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

const SchliessenIcon = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);

/** Was innerhalb der Karte den Fokus annehmen darf (für die Fokusfalle). */
const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Props = {
  open: boolean;
  /** Überschrift, z. B. „Eigentümer auswählen". */
  titel: string;
  /**
   * Vorauswahl der Neuanlage-Maske. Gesucht wird immer über beide Typen —
   * ein Eigentümer, Mieter oder Verwalter kann Person ODER Firma sein.
   */
  defaultType?: "Person" | "Firma";
  onClose: () => void;
  /** Der gewählte bzw. neu angelegte Kontakt (Rohobjekt der API). */
  onSelect: (contact: unknown) => void;
};

const KontaktAuswahlPopup = ({
  open,
  titel,
  defaultType = "Person",
  onClose,
  onSelect,
}: Props) => {
  const karteRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    alleDokumente.addEventListener("keydown", beiTaste);
    return () => alleDokumente.removeEventListener("keydown", beiTaste);
  }, [open, onClose]);

  // Den Auslöser für die Fokus-Rückgabe im RENDER merken, nicht im Effekt:
  // Das Suchfeld trägt `autoFocus` (RelationContactPicker), und React setzt
  // den Fokus dorthin bereits in der Commit-Phase — also bevor irgendein
  // Effekt dieser Komponente läuft. Im Effekt gemessen wäre „vorher" schon
  // das Suchfeld selbst, und beim Schließen bliebe der Fokus auf `<body>`.
  const ausloeserRef = React.useRef<HTMLElement | null>(null);
  if (open && !ausloeserRef.current) {
    ausloeserRef.current = aktivesDokument().activeElement as HTMLElement | null;
  }

  // Fokus beim Öffnen in die Karte. Steht er durch `autoFocus` schon drin,
  // wird er nicht noch einmal verschoben.
  React.useEffect(() => {
    if (!open) return;
    const karte = karteRef.current;
    if (karte && !karte.contains(aktivesDokument().activeElement)) {
      karte.querySelector<HTMLElement>(FOKUSSIERBAR)?.focus();
    }
  }, [open]);

  // Fokus beim Schließen zurück auf den Auslöser. Bewusst NICHT als Cleanup
  // des Effekts darüber: unter React.StrictMode wird jeder Effekt beim Mount
  // einmal auf- und wieder abgebaut — die Rückgabe liefe dann sofort und der
  // gemerkte Auslöser wäre beim echten Schließen schon verbraucht.
  const warOffen = React.useRef(false);
  React.useEffect(() => {
    if (open) {
      warOffen.current = true;
      return;
    }
    if (!warOffen.current) return;
    warOffen.current = false;
    const ausloeser = ausloeserRef.current;
    ausloeserRef.current = null;
    // Ist der Auslöser inzwischen ausgehängt (Formularwechsel), läuft das ins
    // Leere statt auf einen abgehängten Knoten.
    if (ausloeser && document.contains(ausloeser)) ausloeser.focus();
  }, [open]);

  // Scroll-Sperre im Hintergrund, solange das Modal offen ist — dasselbe
  // Muster wie in `NewRiskDialog` / `AddRiskToContractDialog`.
  React.useEffect(() => {
    if (!open) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = vorher;
    };
  }, [open]);

  /** Fokusfalle: Tab/Shift+Tab am letzten/ersten Element umbrechen. */
  const beiTabInKarte = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const ziele = karteRef.current?.querySelectorAll<HTMLElement>(FOKUSSIERBAR);
    if (!ziele || ziele.length === 0) return;
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    if (e.shiftKey && aktivesDokument().activeElement === erstes) {
      e.preventDefault();
      letztes.focus();
    } else if (!e.shiftKey && aktivesDokument().activeElement === letztes) {
      e.preventDefault();
      erstes.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="kvp-rd kvp-rd-scrim"
      // Der z-index aus `contactRedesign.css` (1400) reicht hier NICHT: die
      // Risiko-Formulare laufen im Fenster-Layer (WindowLayer, z-index 9000),
      // und zwischen `document.body` und diesem Layer liegt kein
      // Stacking-Context. Ohne den Inline-Wert malt die opake Fenster-Karte
      // über Scrim und Popup — der Auswahl-Knopf wirkte tot.
      style={{ zIndex: Z_LAYERS.ueberFenster }}
      onClick={onClose}
    >
      <div
        ref={karteRef}
        className="dm"
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        style={{ width: "min(520px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={beiTabInKarte}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Kontakt</div>
              <h2>{titel}</h2>
            </div>
            <button className="dm-x" type="button" onClick={onClose} aria-label="Schließen">
              {SchliessenIcon}
            </button>
          </div>
        </div>
        <div className="dm-body">
          {/* `selected` bleibt bewusst `null`: Ein Treffer wird sofort
              übernommen und das Popup schließt. Der Zwischenschritt
              „ausgewählt — jetzt speichern" aus dem Verknüpfungs-Dialog wäre
              hier ein Klick zu viel, denn gespeichert wird erst mit dem
              Formular. */}
          <RelationContactPicker
            defaultType={defaultType}
            selected={null}
            onSelect={(c) => {
              if (c) onSelect(c);
            }}
          />
        </div>
        <div className="dm-foot">
          <button type="button" className="rd-pop-btn" onClick={onClose}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default KontaktAuswahlPopup;
